"use client";

import React, { CSSProperties } from "react";
import styled, { DefaultTheme } from "styled-components";

import {
  positioningCSS,
  flexCSS,
  getColor,
  getBorderRadius,
  getFont,
  getCustomFontFamily,
  transitionCSS,
} from "../utils";

import { ellipsis } from "../utils";
import Flex from "../Flex";
import Icon from "../Icon";
import LinkWrapper from "../LinkWrapper";

import { ElementPositioningProps, ElementFlexProps, VariableComponent } from "../ComponentProps";
import ActivityIndicator from "../ActivityIndicator";

export enum ButtonStyle {
  primary = "primary",
  secondary = "secondary",
  tertiary = "tertiary",
  outline = "outline",
}

enum ButtonSize {
  small = "small",
  regular = "regular",
}

const buttonHeights = {
  [ButtonSize.regular]: "36px",
  [ButtonSize.small]: "28px",
};

export type Props = VariableComponent<
  {
    id?: string;
    type?: "button" | "submit";
    text?: string;
    icon?: string;
    iconBefore?: string;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    onDoubleClick?: React.MouseEventHandler<HTMLButtonElement>;
    buttonStyle?: "primary" | "secondary" | "tertiary" | "outline";
    link?: string;
    title?: string;
    newWindow?: boolean;
    truncate?: boolean;
    fullWidth?: boolean;
    size?: "small" | "regular";
    themeOverrides?: {
      primaryColor?: string;
      primaryColorDark?: string;
      primaryColorTint?: string;
      outlineStyle?: "light" | "dark";
      customOutlineColor?: string;
    };
    fontStyle?: string;
    background?: string; // Override background color
    tintColor?: string;
    dataPopupSource?: string; // For tooltip
    tabIndex?: number;
    style?: CSSProperties;
  } & ElementPositioningProps &
    ElementFlexProps
>;

type ThemedProps = Props & { theme: DefaultTheme };

function getTintColor(props: ThemedProps, hover = false) {
  const color = getColor(props.tintColor);
  if (color) return color;
  const theme = { ...props.theme, ...props.themeOverrides };
  switch (props.buttonStyle) {
    case "primary":
      return hover ? null : getColor(theme.primaryColorTint, "white");
    case "secondary":
      return hover ? null : getColor("black");
    case "tertiary":
      return hover
        ? getColor(theme.primaryColorDark, "primaryDark")
        : getColor(theme.primaryColor, "primary");
    case "outline":
      if (hover) return getColor("black");
      return getColor(props.themeOverrides?.outlineStyle === "dark" ? "white" : "black");
    default:
      return getColor("black");
  }
}

function getBackgroundColor(props: ThemedProps, hover: boolean) {
  const color = getColor(props.background);
  if (color) return color;

  const theme = { ...props.theme, ...props.themeOverrides };
  switch (props.buttonStyle) {
    case "primary":
      return hover
        ? getColor(theme.primaryColorDark, "primaryDark")
        : getColor(theme.primaryColor, "primary");
    case "secondary":
      return hover ? getColor("gray") : getColor("grayLight");
    case "outline":
      return hover ? getColor("grayLighter") : "transparent";
    case "tertiary":
    default:
      return null;
  }
}
type InternalProps = Props & {
  iconOnly: boolean;
};

const ButtonBase = styled.button<InternalProps>`
  background: ${props => getBackgroundColor(props, false)};
  color: ${props => getTintColor(props)};
  border: none;
  ${props =>
    getFont(
      props.fontStyle,
      props.size === "small" ? "ui.smallBold" : "ui.regularSemiBold",
      props
    )};
  cursor: pointer;
  pointer-events: ${props => (props.disabled ? "none" : null)};
  ${transitionCSS({
    transitionProperty: "background, opacity, color, text-decoration, box-shadow",
    transitionDuration: "0.2s",
  })}
  opacity: ${props => (props.disabled ? ".5" : "1")};
  height: ${props => buttonHeights[props.size]};
  user-select: none;
  box-shadow: none;

  span {
    font-family: ${props => getCustomFontFamily(props)};
  }

  div,
  svg,
  span {
    pointer-events: none;
  }

  svg {
    fill: ${props => getTintColor(props, false)};
    ${transitionCSS({
      transitionProperty: "fill",
    })}
  }
  &:hover {
    background: ${props => getBackgroundColor(props, true)};
    color: ${props => getTintColor(props, true)};
    svg {
      fill: ${props => getTintColor(props, true)};
    }
  }
`;

const Wrapped = styled(ButtonBase).attrs({ ...ellipsis })`
  border-radius: ${props => getBorderRadius("default", null, props)};
  width: ${props => {
    if (props.fullWidth) return "100%";
    if (props.iconOnly) return buttonHeights[props.size];
  }};
  ${props =>
    props.iconOnly ? null : positioningCSS({ px: props.size === "small" ? "m" : "l", py: "xs" })}
  ${positioningCSS}
  ${flexCSS}
`;

const Outline = styled(ButtonBase).attrs({ ...ellipsis })`
  border-radius: ${props => getBorderRadius("default", null, props)};
  background: ${props => getBackgroundColor(props, false)};
  border: 1px solid
    ${props => {
      if (props.themeOverrides?.outlineStyle) {
        return getColor(props.themeOverrides?.outlineStyle === "dark" ? "white" : "black");
      }
      if (props.themeOverrides?.customOutlineColor) {
        return getColor(props.themeOverrides?.customOutlineColor);
      }
      return getColor("black");
    }};
  width: ${props => {
    if (props.fullWidth) return "100%";
    if (props.iconOnly) return buttonHeights[props.size];
  }};
  ${props => (props.iconOnly ? null : positioningCSS({ px: "m", py: "xs" }))}
  ${positioningCSS}
  ${flexCSS}
  &:hover {
    background: ${props => getBackgroundColor(props, true)};
  }
`;

const Tertiary = styled(ButtonBase)`
  ${ellipsis}
  background: none;
  padding: 0;
  height: auto;
  min-width: 0;
  ${props => getFont(props.fontStyle, props.size === "small" ? "ui.small" : "ui.regular", props)};
  ${positioningCSS}
  ${flexCSS}
  &:hover {
    text-decoration: underline;
  }
`;

const Button = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    id,
    type = "button",
    text,
    icon,
    loading,
    iconBefore,
    onClick,
    onDoubleClick,
    buttonStyle = "primary",
    link,
    title,
    newWindow,
    truncate,
    size = ButtonSize.regular,
    themeOverrides = {},
    ...style
  } = props;

  const iconOnly = !(text && text.length) && Boolean(icon),
    linkProps = {
      disabled: props.disabled,
      link,
      target: newWindow ? "_blank" : undefined,
      // allow setting overflow hidden for tertiary buttons to truncate buttons
      style: {
        flexShrink: style.flexShrink,
        ...(buttonStyle === "tertiary" && truncate
          ? { overflow: "hidden", display: "contents" }
          : {}),
      },
    };

  function renderContent() {
    if (!icon && !iconBefore) return <span>{text}</span>;

    const truncateCss = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    } as React.CSSProperties;

    return (
      <Flex justifyContent="center" alignItems="center">
        {loading ? (
          <ActivityIndicator size="extraSmall" />
        ) : (
          <>
            {iconBefore && (
              <Icon size={size === "small" ? "16" : "24"} mr="xs" iconId={iconBefore} />
            )}
            {text && <span style={truncate ? truncateCss : undefined}>{text}</span>}
            {icon && (
              <Icon
                size={size === "small" ? "16" : "24"}
                ml={text ? "xs" : undefined}
                iconId={icon}
              />
            )}
          </>
        )}
      </Flex>
    );
  }

  function renderInner() {
    const buttonProps = {
      ...style,
      ref,
      buttonStyle,
      type,
      title,
      iconOnly,
      size,
      onClick,
      disabled: props.disabled,
      onDoubleClick,
      truncate,
      themeOverrides,
    };
    switch (buttonStyle) {
      case "tertiary":
        return (
          <Tertiary
            data-testid={id}
            data-popup-source={buttonProps?.dataPopupSource}
            {...buttonProps}>
            {renderContent()}
          </Tertiary>
        );
      case "outline":
        return (
          <Outline
            data-testid={id}
            data-popup-source={buttonProps?.dataPopupSource}
            {...buttonProps}>
            {renderContent()}
          </Outline>
        );
      default:
        return (
          <Wrapped
            data-testid={id}
            data-popup-source={buttonProps?.dataPopupSource}
            {...buttonProps}>
            {renderContent()}
          </Wrapped>
        );
    }
  }

  return <LinkWrapper {...linkProps}>{renderInner()}</LinkWrapper>;
});

Button.displayName = "Button";

export default Button;
